/**
 * ODS typography tokens from `@telekom-ods/react-ui-kit/style.css`).
 *
 * Prefer `odsTextStyle` for Material-style objects: same tiers as `--body-m-regular` / title shorthands
 * (family + weight + line height from one design-system scale).
 */

const tier = <const P extends string>(
  prefix: P,
): {
  fontFamily: `var(--${P}-font-family)`;
  fontWeight: `var(--${P}-font-weight)`;
  lineHeight: `var(--${P}-line-height)`;
} => ({
  fontFamily: `var(--${prefix}-font-family)` as `var(--${P}-font-family)`,
  fontWeight: `var(--${prefix}-font-weight)` as `var(--${P}-font-weight)`,
  lineHeight: `var(--${prefix}-line-height)` as `var(--${P}-line-height)`,
});

export const odsTextStyle = {
  titleXl: tier("title-xl"),
  titleL: tier("title-l"),
  titleM: tier("title-m"),
  titleS: tier("title-s"),
  bodyMRegular: tier("body-m-regular"),
  bodyMBold: tier("body-m-bold"),
  bodyL: tier("body-l"),
  bodySRegular: tier("body-s-regular"),
  bodySBold: tier("body-s-bold"),
  display: tier("display"),
  subtitle: tier("subtitle"),
} as const;

/** Callers that only need `fontFamily`; `odsTextStyle` carries the full trio. */
export const odsFontFamily = {
  display: odsTextStyle.display.fontFamily,
  subtitle: odsTextStyle.subtitle.fontFamily,
  titleXl: odsTextStyle.titleXl.fontFamily,
  titleL: odsTextStyle.titleL.fontFamily,
  titleM: odsTextStyle.titleM.fontFamily,
  titleS: odsTextStyle.titleS.fontFamily,
  bodyL: odsTextStyle.bodyL.fontFamily,
  bodyMRegular: odsTextStyle.bodyMRegular.fontFamily,
  bodyMBold: odsTextStyle.bodyMBold.fontFamily,
  bodySRegular: odsTextStyle.bodySRegular.fontFamily,
  bodySBold: odsTextStyle.bodySBold.fontFamily,
} as const;

export const odsFontShorthand = {
  bodyMediumRegular: "var(--body-m-regular)",
} as const;

export const getTypography = (fontSize: number) => ({
  ...odsTextStyle.bodyMRegular,

  h1: {
    ...odsTextStyle.titleXl,
    fontSize: `${fontSize * 2.5}px`,
  },
  h2: {
    ...odsTextStyle.titleL,
    fontSize: `${fontSize * 2}px`,
  },
  h3: {
    ...odsTextStyle.titleM,
    fontSize: "1.75rem",
  },
  h4: {
    ...odsTextStyle.titleS,
    fontSize: "1.5rem",
  },
  subtitle1: {
    ...odsTextStyle.subtitle,
    fontSize: `${(fontSize / 16) * 1.125}rem`,
  },
  body1: {
    ...odsTextStyle.bodyMRegular,
    fontSize: `${fontSize / 16}rem`,
  },
  body2: {
    ...odsTextStyle.bodyMBold,
    fontSize: `${fontSize / 16}rem`,
  },
  bodySmall: {
    ...odsTextStyle.bodySRegular,
    fontSize: `${(fontSize / 16) * 0.875}rem`,
  },
  caption: {
    ...odsTextStyle.bodySRegular,
    fontSize: `${(fontSize / 16) * 0.875}rem`,
  },
  button: {
    ...odsTextStyle.bodyMBold,
    fontSize: `${fontSize / 16}rem`,
  },
  overline: {
    ...odsTextStyle.bodySBold,
    fontSize: `${(fontSize / 16) * 0.75}rem`,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
  },
});
